import React, { useState, useEffect } from 'react';
import { X, Loader2, Copy, CheckCircle, AlertCircle, FileText, Youtube, RefreshCw, Download, Share2, Mail, Globe, Book, Target, MessageSquare, Lightbulb, BookOpen } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { generateStudyQuestions, generateSermonSummary } from '../lib/openai';
import { jsPDF } from 'jspdf';
import { createStyledPDF, addSection, cleanAIText, cleanAIArray } from '../lib/pdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import { format, parseISO } from 'date-fns';

interface SermonShareModalProps {
  sermonId: string;
  sermonTitle: string;
  sermonScripture: string;
  sermonContent: string;
  youtubeUrl: string;
  sermonDate: string;
  onClose: () => void;
}

export function SermonShareModal({
  sermonId,
  sermonTitle,
  sermonScripture,
  sermonContent,
  youtubeUrl,
  sermonDate,
  onClose
}: SermonShareModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [generatingCommentary, setGeneratingCommentary] = useState(false);
  const [generatingApplications, setGeneratingApplications] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [questions, setQuestions] = useState<string[] | null>(null);
  const [commentary, setCommentary] = useState<string | null>(null);
  const [applications, setApplications] = useState<any[] | null>(null);
  const [step, setStep] = useState<'initial' | 'generating' | 'complete'>('initial');
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const [smallGroupGuideUrl, setSmallGroupGuideUrl] = useState<string | null>(null);
  const [personalStudyGuideUrl, setPersonalStudyGuideUrl] = useState<string | null>(null);

  const addDebugInfo = (info: string) => {
    setDebugInfo(prev => [...prev, `[${new Date().toISOString()}] ${info}`]);
    console.log(`Debug: ${info}`);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch (e) {
      return '';
    }
  };

  useEffect(() => {
    const checkExistingShare = async () => {
      try {
        addDebugInfo('Checking for existing share');
        const { data, error: fetchError } = await supabase
          .from('shared_sermons')
          .select('id, summary, questions, small_group_guide_url, personal_study_guide_url')
          .eq('sermon_id', sermonId)
          .maybeSingle();

        if (fetchError) {
          addDebugInfo(`Error checking existing share: ${fetchError.message}`);
          throw fetchError;
        }

        if (data) {
          addDebugInfo('Found existing share');
          const shareUrl = `${window.location.origin}/shared/${data.id}`;
          setShareUrl(shareUrl);
          setStep('initial'); // Show initial step with existing share UI

          if (data.summary) {
            addDebugInfo('Setting existing summary');
            setSummary(data.summary);
          }
          if (data.questions) {
            addDebugInfo('Setting existing questions');
            setQuestions(data.questions);
          }

          // Use existing study guide URLs if available
          if (data.small_group_guide_url) {
            setSmallGroupGuideUrl(data.small_group_guide_url);
          }
          if (data.personal_study_guide_url) {
            setPersonalStudyGuideUrl(data.personal_study_guide_url);
          }

          // Generate study guides if they don't exist
          if (data.summary && data.questions && (!data.small_group_guide_url || !data.personal_study_guide_url)) {
            addDebugInfo('Generating missing PDFs for existing share');

            try {
              const commentaryResult = await generateCommentary(sermonContent);
              const applicationsResult = await generateApplicationPoints(sermonContent);

              setCommentary(commentaryResult);
              setApplications(applicationsResult);

              if (!data.small_group_guide_url) {
                const smallGroupGuide = createSmallGroupGuidePDF(
                  sermonTitle,
                  sermonScripture,
                  data.summary,
                  data.questions
                );
                const smallGroupUrl = URL.createObjectURL(smallGroupGuide);
                setSmallGroupGuideUrl(smallGroupUrl);
              }

              if (!data.personal_study_guide_url) {
                const personalStudyGuide = createPersonalStudyGuidePDF(
                  sermonTitle,
                  sermonScripture,
                  data.summary,
                  commentaryResult,
                  applicationsResult
                );
                const personalUrl = URL.createObjectURL(personalStudyGuide);
                setPersonalStudyGuideUrl(personalUrl);
              }
            } catch (err) {
              console.error('Error generating additional content for existing share:', err);
              // Still create basic guides if they don't exist
              if (!data.small_group_guide_url) {
                const smallGroupGuide = createSmallGroupGuidePDF(
                  sermonTitle,
                  sermonScripture,
                  data.summary,
                  data.questions
                );
                setSmallGroupGuideUrl(URL.createObjectURL(smallGroupGuide));
              }
              if (!data.personal_study_guide_url) {
                const personalStudyGuide = createPersonalStudyGuidePDF(
                  sermonTitle,
                  sermonScripture,
                  data.summary,
                  "This sermon explores key theological themes that resonate throughout Scripture.",
                  []
                );
                setPersonalStudyGuideUrl(URL.createObjectURL(personalStudyGuide));
              }
            }
          }
        } else {
          addDebugInfo('No existing share found');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        addDebugInfo(`Error in checkExistingShare: ${errorMessage}`);
        console.error('Error checking existing share:', err);
      }
    };

    checkExistingShare();
  }, [sermonId, sermonContent]);

  const filterVisibleContent = (htmlContent: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const elements = Array.from(doc.body.children);

    // Filter out hidden sections
    const visibleElements = elements.filter(element => {
      if (element.classList.contains('sermon-section')) {
        const sectionId = element.getAttribute('data-section-id');
        if (sectionId) {
          const savedState = localStorage.getItem(`sermon-section-visibility-${sermonId}-${sectionId}`);
          const isVisible = savedState !== null ? JSON.parse(savedState) : true;
          return isVisible;
        }
      }
      return true;
    });

    // Reconstruct HTML with only visible elements
    const newDoc = document.createElement('div');
    visibleElements.forEach(element => {
      newDoc.appendChild(element.cloneNode(true));
    });
    return newDoc.innerHTML;
  };

  const generateCommentary = async (content: string): Promise<string> => {
    if (!content) {
      addDebugInfo('Content is empty in generateCommentary');
      // Return fallback instead of throwing error
      return `This sermon on "${sermonTitle}" from ${sermonScripture || 'Scripture'} explores important theological themes that are relevant to our daily Christian walk. The message provides both historical context and practical wisdom for living out our faith today.`;
    }

    try {
      addDebugInfo('Starting commentary generation');
      setGeneratingCommentary(true);

      // Filter content to only include visible sections
      const filteredContent = filterVisibleContent(content);
      const textContent = new DOMParser().parseFromString(filteredContent, 'text/html').body.textContent ?? '';

      if (!textContent) {
        addDebugInfo('No text content extracted from HTML');
        throw new Error('No text content available for commentary generation');
      }

      const maxChars = 10000;
      const truncatedContent = textContent.length > maxChars 
        ? textContent.slice(0, maxChars) + '\n\n[Content truncated for processing]'
        : textContent;

      addDebugInfo('Sending request to secure AI service');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !anonKey) {
        addDebugInfo('Missing Supabase environment variables');
        throw new Error('Missing Supabase configuration');
      }

      addDebugInfo(`Making request to: ${supabaseUrl}/functions/v1/ai-analysis`);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        addDebugInfo('Request timeout - aborting after 30 seconds');
        controller.abort();
      }, 30000); // Reduced to 30 seconds for mobile compatibility

      const response = await fetch(`${supabaseUrl}/functions/v1/ai-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': anonKey,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({
          type: 'enhance',
          content: `Sermon Title: ${sermonTitle}
Scripture: ${sermonScripture}
Content: ${truncatedContent}

Create a brief commentary on the sermon that includes:
1. Historical and cultural context for the scripture passages
2. Theological insights that deepen understanding
3. Connections to other relevant scripture passages
4. Brief explanations of any complex theological concepts

Format your response as a well-structured commentary of about 300-400 words.`,
          enhancementType: 'theological'
        }),
        signal: controller.signal,
        mode: 'cors',
        credentials: 'include'
      });

      clearTimeout(timeoutId);

      addDebugInfo(`Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
          addDebugInfo(`Error response: ${errorText}`);
        } catch (e) {
          errorText = `HTTP ${response.status}: ${response.statusText}`;
        }
        addDebugInfo(`AI service error: ${response.status} ${errorText}`);
        throw new Error(`AI service unavailable: ${response.status} - ${errorText}`);
      }

      let data;
      try {
        data = await response.json();
      } catch (e) {
        addDebugInfo('Invalid JSON response from AI service');
        throw new Error('Invalid response format from AI service');
      }

      const commentaryContent = data.content;

      if (!data.success || !commentaryContent) {
        addDebugInfo('No commentary content in AI service response');
        throw new Error(data.error || 'No commentary content received from AI service');
      }

      addDebugInfo('Successfully generated commentary');
      return commentaryContent;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addDebugInfo(`Error in generateCommentary: ${errorMessage}`);

      // Check for mobile-specific network errors
      if (error instanceof Error && 
          (error.name === 'AbortError' || 
           error.message.includes('AbortError') ||
           error.message.includes('NetworkError') ||
           error.message.includes('Failed to fetch') ||
           error.message.includes('The operation was aborted'))) {
        addDebugInfo('Mobile network error detected, using fallback');
      }

      // Return contextual fallback instead of throwing
      addDebugInfo('Using contextual fallback commentary');
      return `This sermon on "${sermonTitle}" from ${sermonScripture || 'Scripture'} explores important theological themes that resonate throughout the biblical narrative. The passage highlights God's sovereignty and grace, reminding us of His faithfulness throughout history. The historical context of this scripture reveals God's heart for His people and provides practical wisdom for our daily walk with Christ. This message connects to broader biblical themes of redemption, hope, and God's transformative love in our lives.`;
    } finally {
      setGeneratingCommentary(false);
    }
  };

  const generateApplicationPoints = async (content: string): Promise<{ title: string, description: string }[]> => {
    if (!content) {
      addDebugInfo('Content is empty in generateApplicationPoints');
      // Return fallback instead of throwing error
      return [
        {
          title: "Daily Scripture Reflection",
          description: `Set aside time each morning to read and meditate on ${sermonScripture || 'the key passages'} from this sermon, asking God to reveal how they apply to your current circumstances.`
        },
        {
          title: "Apply Biblical Principles",
          description: "Identify one specific area of your life where you can apply the main teaching from this message this week, and take concrete steps toward biblical obedience."
        },
        {
          title: "Share Gospel Truths",
          description: "Find an opportunity to share one key insight from this sermon with a friend, family member, or colleague, opening the door for spiritual conversation."
        },
        {
          title: "Prayer and Surrender",
          description: "Spend time in prayer asking God to help you understand and live out the truths presented in this message, surrendering areas of resistance or doubt."
        },
        {
          title: "Community Connection",
          description: "Reach out to someone from your church community to discuss this sermon together, encouraging one another in practical faith application."
        }
      ];
    }

    try {
      addDebugInfo('Starting application points generation');
      setGeneratingApplications(true);

      // Filter content to only include visible sections
      const filteredContent = filterVisibleContent(content);
      const textContent = new DOMParser().parseFromString(filteredContent, 'text/html').body.textContent ?? '';

      if (!textContent) {
        addDebugInfo('No text content extracted from HTML');
        throw new Error('No text content available for application points generation');
      }

      const maxChars = 10000;
      const truncatedContent = textContent.length > maxChars
        ? textContent.slice(0, maxChars) + '\n\n[Content truncated for processing]'
        : textContent;

      addDebugInfo('Sending request to secure AI service');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !anonKey) {
        addDebugInfo('Missing Supabase environment variables for applications');
        throw new Error('Missing Supabase configuration');
      }

      addDebugInfo(`Making applications request to: ${supabaseUrl}/functions/v1/ai-analysis`);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        addDebugInfo('Request timeout - aborting application points request after 30 seconds');
        controller.abort();
      }, 30000); // Reduced to 30 seconds for mobile compatibility

      const response = await fetch(`${supabaseUrl}/functions/v1/ai-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': anonKey,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({
          type: 'enhance',
          content: `Sermon Title: ${sermonTitle}
Scripture: ${sermonScripture}
Content: ${truncatedContent}

Generate 5 practical application points in JSON format. Each point should have a "title" and "description" field. Return as an array of objects.`,
          enhancementType: 'application'
        }),
        signal: controller.signal,
        mode: 'cors',
        credentials: 'include'
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
        } catch (e) {
          errorText = `HTTP ${response.status}: ${response.statusText}`;
        }
        addDebugInfo(`AI service error: ${response.status} ${errorText}`);
        throw new Error(`AI service unavailable: ${response.status} - ${errorText}`);
      }

      let data;
      try {
        data = await response.json();
      } catch (e) {
        addDebugInfo('Invalid JSON response from AI service');
        throw new Error('Invalid response format from AI service');
      }

      const aiContent = data.content;

      if (!data.success || !aiContent) {
        addDebugInfo('No content returned from AI service for application points');
        throw new Error(data.error || 'No content received from AI service');
      }

      addDebugInfo('Attempting to parse response');

      // Try multiple parsing approaches
      let parsedApplications;

      try {
        // First attempt: direct parse
        const cleanedContent = aiContent
          .replace(/```json\n?/g, '')
          .replace(/```\n?/g, '')
          .trim();

        addDebugInfo('Parsing cleaned JSON response: ' + cleanedContent.substring(0, 100) + '...');
        parsedApplications = JSON.parse(cleanedContent);

        // Check if we got an object with an array property
        if (!Array.isArray(parsedApplications)) {
          // Look for common property names that might contain the array
          if (parsedApplications.applications && Array.isArray(parsedApplications.applications)) {
            parsedApplications = parsedApplications.applications;
          } else if (parsedApplications.points && Array.isArray(parsedApplications.points)) {
            parsedApplications = parsedApplications.points;
          } else {
            // Try to find any array property
            for (const key in parsedApplications) {
              if (Array.isArray(parsedApplications[key])) {
                parsedApplications = parsedApplications[key];
                break;
              }
            }
          }
        }

        // If we still don't have an array, throw an error
        if (!Array.isArray(parsedApplications)) {
          throw new Error('Parsed result is not an array');
        }

        addDebugInfo('Successfully parsed application points JSON');
      } catch (parseError) {
        addDebugInfo(`JSON parse error: ${parseError instanceof Error ? parseError.message : 'Unknown parse error'}`);
        throw parseError;
      }

      // Validate and format the application points
      const validatedApplications = parsedApplications.map((point: any, index: number) => {
        // Ensure each point has title and description
        if (!point || typeof point !== 'object') {
          return {
            title: `Application Point ${index + 1}`,
            description: "Apply the principles from this sermon to your daily life."
          };
        }

        return {
          title: typeof point.title === 'string' && point.title.trim() 
            ? point.title.trim() 
            : `Application Point ${index + 1}`,
          description: typeof point.description === 'string' && point.description.trim()
            ? point.description.trim()
            : "Apply the principles from this sermon to your daily life."
        };
      });

      // Ensure we have exactly 5 points
      while (validatedApplications.length < 5) {
        validatedApplications.push({
          title: `Application Point ${validatedApplications.length + 1}`,
          description: "Apply the principles from this sermon to your daily life."
        });
      }

      // Trim to 5 points if we have more
      if (validatedApplications.length > 5) {
        validatedApplications.length = 5;
      }

      return validatedApplications;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addDebugInfo(`Error in generateApplicationPoints: ${errorMessage}`);

      // Check for mobile-specific network errors
      if (error instanceof Error && 
          (error.name === 'AbortError' || 
           error.message.includes('AbortError') ||
           error.message.includes('NetworkError') ||
           error.message.includes('Failed to fetch') ||
           error.message.includes('The operation was aborted'))) {
        addDebugInfo('Mobile network error detected for application points, using fallback');
      }

      // Return contextual fallback instead of throwing
      addDebugInfo('Using contextual fallback application points');
      return [
        {
          title: "Daily Scripture Reflection",
          description: `Set aside time each morning to read and meditate on ${sermonScripture || 'the key passages'} from this sermon, asking God to reveal how they apply to your current circumstances.`
        },
        {
          title: "Apply Biblical Principles",
          description: "Identify one specific area of your life where you can apply the main teaching from this message this week, and take concrete steps toward biblical obedience."
        },
        {
          title: "Share Gospel Truths",
          description: "Find an opportunity to share one key insight from this sermon with a friend, family member, or colleague, opening the door for spiritual conversation."
        },
        {
          title: "Prayer and Surrender",
          description: "Spend time in prayer asking God to help you understand and live out the truths presented in this message, surrendering areas of resistance or doubt."
        },
        {
          title: "Community Connection",
          description: "Reach out to someone from your church community to discuss this sermon together, encouraging one another in practical faith application."
        }
      ];
    } finally {
      setGeneratingApplications(false);
    }
  };

  const createSmallGroupGuidePDF = (
    title: string,
    scripture: string,
    summary: string,
    questions: string[]
  ): Blob => {
    const doc = new jsPDF();
    const W = doc.internal.pageSize.width;
    const H = doc.internal.pageSize.height;
    const ml = 20;
    const mr = W - ml;
    const contentW = mr - ml;
    const lineH = 6.5;
    let y = 20;

    // ── HEADER ────────────────────────────────────────────────────────────────
    // Brand rule
    doc.setDrawColor(30, 64, 175);
    doc.setLineWidth(1.2);
    doc.line(ml, y, mr, y);
    y += 9;

    // Document type + title
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(148, 163, 184);
    doc.text('SMALL GROUP', W / 2, y, { align: 'center' });
    y += 6;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(15, 23, 42);
    doc.text('Discussion Guide', W / 2, y, { align: 'center' });
    y += 11;

    if (title) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(71, 85, 105);
      const tl = doc.splitTextToSize(title, contentW);
      doc.text(tl, W / 2, y, { align: 'center' });
      y += tl.length * 7 + 4;
    }

    if (scripture) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(148, 163, 184);
      doc.text(scripture, W / 2, y, { align: 'center' });
      y += 6;
    }

    y += 5;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.4);
    doc.line(ml, y, mr, y);
    y += 12;

    const sectionHeader = (label: string, color: [number, number, number]) => {
      if (y > H - 60) { doc.addPage(); y = ml; }
      doc.setFillColor(color[0], color[1], color[2]);
      doc.rect(ml - 5, y - 5.5, 3, 11, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(color[0], color[1], color[2]);
      doc.text(label, ml + 1, y);
      y += 8;
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(ml, y - 2, mr, y - 2);
      y += 7;
    };

    // ── SERMON SUMMARY ────────────────────────────────────────────────────────
    if (summary) {
      sectionHeader('SERMON SUMMARY', [30, 64, 175]);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      const sl = doc.splitTextToSize(summary, contentW);
      if (y + sl.length * lineH > H - 28) { doc.addPage(); y = ml; }
      doc.text(sl, ml, y);
      y += sl.length * lineH + 14;
    }

    // ── DISCUSSION QUESTIONS ──────────────────────────────────────────────────
    if (questions?.length) {
      if (y > H - 80) { doc.addPage(); y = ml; }
      sectionHeader('DISCUSSION QUESTIONS', [159, 18, 57]);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);

      questions.forEach((q, i) => {
        const qLines = doc.splitTextToSize(`${i + 1}.  ${q}`, contentW - 4);
        const blockH = qLines.length * lineH + 3 * 9 + 10;
        if (y + blockH > H - 28) { doc.addPage(); y = ml; }

        doc.text(qLines, ml + 4, y);
        y += qLines.length * lineH + 5;

        for (let n = 0; n < 3; n++) {
          doc.setDrawColor(210, 218, 226);
          doc.setLineWidth(0.25);
          doc.line(ml + 6, y + 4, mr, y + 4);
          y += 9;
        }
        y += 7;
      });
    }

    // ── PERSONAL NOTES ────────────────────────────────────────────────────────
    if (y > H - 80) { doc.addPage(); y = ml; }
    sectionHeader('PERSONAL NOTES', [161, 98, 7]);

    for (let i = 0; i < 10; i++) {
      if (y + 13 > H - 28) { doc.addPage(); y = ml; }
      doc.setDrawColor(210, 218, 226);
      doc.setLineWidth(0.25);
      doc.line(ml, y + 6, mr, y + 6);
      y += 14;
    }

    // Footer on every page
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(ml, H - 16, mr, H - 16);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('SermonBuild', ml, H - 10);
      doc.text(`${i} / ${pages}`, W / 2, H - 10, { align: 'center' });
    }

    return doc.output('blob');
  };

  const createPersonalStudyGuidePDF = (
    title: string,
    scripture: string,
    summary: string,
    commentary: string,
    applications: any[]
  ): Blob => {
    const doc = new jsPDF();
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const ml = 20;
    const mr = W - ml;
    const contentW = mr - ml;
    const lineH = 6.5;
    let y = 20;

    // ── HEADER ────────────────────────────────────────────────────────────────
    doc.setDrawColor(30, 64, 175);
    doc.setLineWidth(1.2);
    doc.line(ml, y, mr, y);
    y += 9;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(148, 163, 184);
    doc.text('PERSONAL', W / 2, y, { align: 'center' });
    y += 6;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(15, 23, 42);
    doc.text('Study Guide', W / 2, y, { align: 'center' });
    y += 11;

    if (title) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(71, 85, 105);
      const tl = doc.splitTextToSize(title, contentW);
      doc.text(tl, W / 2, y, { align: 'center' });
      y += tl.length * 7 + 3;
    }

    if (scripture) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(148, 163, 184);
      doc.text(scripture, W / 2, y, { align: 'center' });
      y += 5;
    }

    if (sermonDate) {
      const dateText = parseISO(sermonDate).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(148, 163, 184);
      doc.text(dateText, W / 2, y, { align: 'center' });
      y += 5;
    }

    y += 5;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.4);
    doc.line(ml, y, mr, y);
    y += 12;

    const sectionHeader = (label: string, color: [number, number, number]) => {
      if (y > H - 60) { doc.addPage(); y = ml; }
      doc.setFillColor(color[0], color[1], color[2]);
      doc.rect(ml - 5, y - 5.5, 3, 11, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(color[0], color[1], color[2]);
      doc.text(label, ml + 1, y);
      y += 8;
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(ml, y - 2, mr, y - 2);
      y += 7;
    };

    const renderBlock = (text: string) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      const lines = doc.splitTextToSize(text, contentW);
      if (y + lines.length * lineH > H - 28) { doc.addPage(); y = ml; }
      doc.text(lines, ml, y);
      y += lines.length * lineH + 14;
    };

    // ── SERMON SUMMARY ────────────────────────────────────────────────────────
    if (summary) {
      sectionHeader('SERMON SUMMARY', [21, 128, 61]);
      renderBlock(summary);
    }

    // ── COMMENTARY ────────────────────────────────────────────────────────────
    if (commentary) {
      sectionHeader('COMMENTARY', [159, 18, 57]);
      renderBlock(commentary);
    }

    // ── APPLICATION POINTS ────────────────────────────────────────────────────
    if (applications?.length) {
      sectionHeader('APPLICATION POINTS', [161, 98, 7]);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      applications.forEach((app, i) => {
        const text = `${i + 1}. ${app.title}: ${app.description}`;
        const lines = doc.splitTextToSize(text, contentW);
        if (y + lines.length * lineH + 4 > H - 28) { doc.addPage(); y = ml; }
        doc.text(lines, ml, y);
        y += lines.length * lineH + 6;
      });
      y += 8;
    }

    // ── PERSONAL REFLECTION ───────────────────────────────────────────────────
    if (y > H - 80) { doc.addPage(); y = ml; }
    sectionHeader('PERSONAL REFLECTION', [29, 78, 216]);

    for (let i = 0; i < 10; i++) {
      if (y + 13 > H - 28) { doc.addPage(); y = ml; }
      doc.setDrawColor(210, 218, 226);
      doc.setLineWidth(0.25);
      doc.line(ml, y + 6, mr, y + 6);
      y += 14;
    }

    // Footer on every page
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(ml, H - 16, mr, H - 16);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('SermonBuild', ml, H - 10);
      doc.text(`${i} / ${pages}`, W / 2, H - 10, { align: 'center' });
    }

    return doc.output('blob');
  };

  const handleGenerateSharePage = async () => {
    addDebugInfo('Starting share page generation');
    setStep('generating');
    setLoading(true);
    setError(null);

    try {
      if (!sermonContent) {
        throw new Error('Sermon content is required');
      }

      if (!sermonTitle) {
        throw new Error('Sermon title is required');
      }

      addDebugInfo('Starting parallel content generation');
      setGeneratingSummary(true);
      setGeneratingQuestions(true);
      setGeneratingCommentary(true);
      setGeneratingApplications(true);

      // Filter content to only include visible sections
      const filteredContent = filterVisibleContent(sermonContent);

      const summaryPromise = generateSermonSummary(filteredContent);
      const questionsPromise = generateStudyQuestions(filteredContent);
      const commentaryPromise = generateCommentary(filteredContent);
      const applicationsPromise = generateApplicationPoints(filteredContent);

      addDebugInfo('Waiting for all content generation to complete');
      
      // Add a race condition with timeout for mobile devices
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Mobile timeout: Content generation took too long')), 60000)
      );

      const [summaryResult, questionsResult, commentaryResult, applicationsResult] = await Promise.race([
        Promise.allSettled([
          summaryPromise,
          questionsPromise,
          commentaryPromise,
          applicationsPromise
        ]),
        timeoutPromise
      ]) as PromiseSettledResult<any>[];

      // Handle summary result
      let summaryContent = '';
      if (summaryResult.status === 'fulfilled' && summaryResult.value && summaryResult.value.success) {
        summaryContent = summaryResult.value.content;
        addDebugInfo('Summary generation successful');
      } else {
        const errorMsg = summaryResult.status === 'rejected' ? summaryResult.reason?.message : 'Unknown error';
        addDebugInfo(`Summary generation failed: ${errorMsg}`);
        throw new Error(`Failed to generate summary: ${errorMsg}`);
      }

      // Handle questions result
      let questionsContent: string[] = [];
      if (questionsResult.status === 'fulfilled' && questionsResult.value && questionsResult.value.success) {
        try {
          let parsed = questionsResult.value.content;
          
          // If the content is a string, try to parse it as JSON
          if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed);
          }
          
          if (Array.isArray(parsed)) {
            questionsContent = parsed.map(q => typeof q === 'string' ? q : q.toString()).filter(q => q.trim());
            addDebugInfo(`Questions generation successful - ${questionsContent.length} questions generated`);
          } else {
            throw new Error('Parsed result is not an array');
          }
        } catch (e) {
          addDebugInfo(`Questions parsing failed: ${e instanceof Error ? e.message : 'Unknown error'}`);
          throw new Error(`Failed to parse questions: ${e instanceof Error ? e.message : 'Unknown error'}`);
        }
      } else {
        const errorMsg = questionsResult.status === 'rejected' ? 
          (questionsResult.reason?.message || JSON.stringify(questionsResult.reason)) : 
          'Unknown error';
        addDebugInfo(`Questions generation failed: ${errorMsg}`);
        throw new Error(`Failed to generate questions: ${errorMsg}`);
      }

      // Commentary and applications are handled with their own fallbacks in their functions

      addDebugInfo('Storing generated content in state');
      setSummary(summaryContent);
      setGeneratingSummary(false);

      setQuestions(questionsContent);
      setGeneratingQuestions(false);

      // Handle commentary result
      let commentaryContent = '';
      if (commentaryResult.status === 'fulfilled' && commentaryResult.value) {
        commentaryContent = commentaryResult.value;
        addDebugInfo('Commentary generation successful');
      } else {
        const errorMsg = commentaryResult.status === 'rejected' ? commentaryResult.reason?.message : 'Unknown error';
        addDebugInfo(`Commentary generation failed: ${errorMsg}, using fallback`);
        commentaryContent = `This sermon on "${sermonTitle}" from ${sermonScripture || 'Scripture'} explores important theological themes that resonate throughout the biblical narrative.`;
      }
      setCommentary(commentaryContent);
      setGeneratingCommentary(false);

      // Handle applications result
      let applicationsContent: any[] = [];
      if (applicationsResult.status === 'fulfilled' && applicationsResult.value && Array.isArray(applicationsResult.value)) {
        applicationsContent = applicationsResult.value;
        addDebugInfo('Applications generation successful');
      } else {
        const errorMsg = applicationsResult.status === 'rejected' ? applicationsResult.reason?.message : 'Unknown error';
        addDebugInfo(`Applications generation failed: ${errorMsg}, using fallback`);
        applicationsContent = [
          {
            title: "Daily Scripture Reflection",
            description: `Set aside time each morning to read and meditate on ${sermonScripture || 'the key passages'} from this sermon.`
          },
          {
            title: "Apply Biblical Principles",
            description: "Identify one specific area of your life where you can apply the main teaching from this message."
          }
        ];
      }
      setApplications(applicationsContent);
      setGeneratingApplications(false);

      addDebugInfo('Getting current user');
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        addDebugInfo(`User authentication error: ${userError.message}`);
        throw userError;
      }
      if (!user) {
        addDebugInfo('User not authenticated');
        throw new Error('Not authenticated');
      }

      // Verify user owns this sermon before proceeding
      addDebugInfo('Verifying sermon ownership');
      const { data: sermonOwnership, error: ownershipError } = await supabase
        .from('sermons')
        .select('user_id')
        .eq('id', sermonId)
        .eq('user_id', user.id)
        .single();

      if (ownershipError) {
        addDebugInfo(`Ownership verification error: ${ownershipError.message}`);
        throw new Error('Unable to verify sermon ownership. You can only share your own sermons.');
      }

      if (!sermonOwnership) {
        addDebugInfo('User does not own this sermon');
        throw new Error('You can only share sermons that you created.');
      }

      addDebugInfo('Checking for existing share');
      const { data: existingShare, error: selectError } = await supabase
        .from('shared_sermons')
        .select('id')
        .eq('sermon_id', sermonId)
        .maybeSingle();

      if (selectError) throw selectError;

      let sharedSermon;

      if (existingShare) {
        addDebugInfo('Updating existing share');
        const { data: updatedShare, error: updateError } = await supabase
          .from('shared_sermons')
          .update({
            summary: summaryContent,
            questions: questionsContent,
            youtube_url: youtubeUrl
          })
          .eq('id', existingShare.id)
          .select()
          .single();

        if (updateError) throw updateError;
        sharedSermon = updatedShare;
      } else {
        addDebugInfo('Creating new share');
        const { data: newShare, error: insertError } = await supabase
          .from('shared_sermons')
          .insert({
            user_id: user.id,
            sermon_id: sermonId,
            summary: summaryContent,
            questions: questionsContent,
            youtube_url: youtubeUrl
          })
          .select()
          .single();

        if (insertError) throw insertError;
        sharedSermon = newShare;
      }

      if (!sharedSermon) throw new Error('Failed to create or update shared sermon');

      addDebugInfo('Creating PDF guides');
      const smallGroupGuide = createSmallGroupGuidePDF(
        sermonTitle,
        sermonScripture,
        summaryContent,
        questionsContent
      );

      const personalStudyGuide = createPersonalStudyGuidePDF(
        sermonTitle,
        sermonScripture,
        summaryContent,
        commentaryContent,
        applicationsContent
      );

      // Create object URLs for the PDFs
      const smallGroupGuideUrl = URL.createObjectURL(smallGroupGuide);
      const personalStudyGuideUrl = URL.createObjectURL(personalStudyGuide);

      setSmallGroupGuideUrl(smallGroupGuideUrl);
      setPersonalStudyGuideUrl(personalStudyGuideUrl);

      // Update the shared sermon with study guide URLs
      try {
        addDebugInfo('Storing study guide URLs in database');
        const { error: updateError } = await supabase
          .from('shared_sermons')
          .update({
            small_group_guide_url: smallGroupGuideUrl,
            personal_study_guide_url: personalStudyGuideUrl
          })
          .eq('id', sharedSermon.id);

        if (updateError) {
          console.error('Error storing study guide URLs:', updateError);
          addDebugInfo(`Error storing URLs: ${updateError.message}`);
        } else {
          addDebugInfo('Successfully stored study guide URLs');
        }
      } catch (err) {
        console.error('Error updating shared sermon with URLs:', err);
        addDebugInfo(`Error updating URLs: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }

      const shareUrl = `${window.location.origin}/shared/${sharedSermon.id}`;
      setShareUrl(shareUrl);
      setStep('complete');
      addDebugInfo('Share page generation complete');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      addDebugInfo(`Error in handleGenerateSharePage: ${errorMessage}`);
      console.error('Error generating share page:', err);
      setError(errorMessage);
      setStep('initial');
    } finally {
      setLoading(false);
      setGeneratingSummary(false);
      setGeneratingQuestions(false);
      setGeneratingCommentary(false);
      setGeneratingApplications(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setError('Failed to copy link to clipboard');
    }
  };

  const handleDownloadSmallGroupGuide = () => {
    if (smallGroupGuideUrl) {
      const a = document.createElement('a');
      a.href = smallGroupGuideUrl;
      a.download = `${sermonTitle.toLowerCase().replace(/\s+/g, '-')}-small-group-guide.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleDownloadPersonalStudyGuide = () => {
    if (personalStudyGuideUrl) {
      const a = document.createElement('a');
      a.href = personalStudyGuideUrl;
      a.download = `${sermonTitle.toLowerCase().replace(/\s+/g, '-')}-personal-study-guide.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-6 border-b border-sand/20 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-midnight">Create Sermon Share Page</h2>
          <button
            onClick={onClose}
            className="p-1 text-slate hover:text-midnight rounded-full"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-crimson/10 text-crimson p-4 rounded-lg mb-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Error generating share page</p>
                  <p className="text-sm mt-1">{error}</p>
                  {debugInfo.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-sm cursor-pointer">Debug Information</summary>
                      <pre className="mt-2 text-xs whitespace-pre-wrap bg-black/5 p-2 rounded">
                        {debugInfo.join('\n')}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 'initial' && (
            <div className="space-y-6">
              {shareUrl ? (
                <div className="bg-emerald/10 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-midnight mb-2">Share Page Already Exists</h3>
                  <p className="text-slate mb-4">
                    This sermon already has a shareable page. You can copy the link below or view the page directly.
                  </p>

                  <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-sand/20 mb-4">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 px-3 py-2 bg-transparent focus:outline-none text-midnight"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="px-3 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 flex items-center"
                      type="button"
                    >
                      {copied ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>

                  <div className="flex justify-center gap-3">
                    <a
                      href={shareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-midnight text-white rounded-lg hover:bg-midnight/90 flex items-center"
                    >
                      View Page
                    </a>
                    <button
                      onClick={() => {
                        setStep('initial');
                        setShareUrl(null);
                        setSummary(null);
                        setQuestions(null);
                        setCommentary(null);
                        setApplications(null);
                        setSmallGroupGuideUrl(null);
                        setPersonalStudyGuideUrl(null);
                      }}
                      className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 flex items-center"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Regenerate Page
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-sand/10 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-midnight mb-2">Create a Shareable Sermon Page</h3>
                    <p className="text-slate mb-4">
                      This will create a public page with your sermon content, an AI-generated summary, and study questions. 
                      You can share this page with your congregation or anyone else.
                    </p>

                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-electric/10 p-2 rounded-lg">
                          <FileText className="h-5 w-5 text-electric" />
                        </div>
                        <div>
                          <h4 className="font-medium text-midnight">Sermon Content</h4>
                          <p className="text-sm text-slate">Your sermon will be formatted for easy reading</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-emerald/10 p-2 rounded-lg">
                          <FileText className="h-5 w-5 text-emerald" />
                        </div>
                        <div>
                          <h4 className="font-medium text-midnight">AI-Generated Summary</h4>
                          <p className="text-sm text-slate">A concise summary of your sermon's key points</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-gold/10 p-2 rounded-lg">
                          <MessageSquare className="h-5 w-5 text-gold" />
                        </div>
                        <div>
                          <h4 className="font-medium text-midnight">Study Questions</h4>
                          <p className="text-sm text-slate">Thought-provoking questions for personal or group study</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-crimson/10 p-2 rounded-lg">
                          <BookOpen className="h-5 w-5 text-crimson" />
                        </div>
                        <div>
                          <h4 className="font-medium text-midnight">Commentary Resources</h4>
                          <p className="text-sm text-slate">Additional theological and historical context</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-midnight/10 p-2 rounded-lg">
                          <Target className="h-5 w-5 text-midnight" />
                        </div>
                        <div>
                          <h4 className="font-medium text-midnight">Personal Application</h4>
                          <p className="text-sm text-slate">Practical application points for daily life</p>
                        </div>
                      </div>

                      {youtubeUrl && (
                        <div className="flex items-start gap-3">
                          <div className="bg-crimson/10 p-2 rounded-lg">
                            <Youtube className="h-5 w-5 text-crimson" />
                          </div>
                          <div>
                            <h4 className="font-medium text-midnight">Video Recording</h4>
                            <p className="text-sm text-slate">Your sermon video will be embedded on the page</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-slate hover:text-midnight"
                      type="button"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleGenerateSharePage}
                      className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90"
                      type="button"
                    >
                      Generate Share Page
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 'generating' && (
            <div className="space-y-6">
              <div className="text-center py-6">
                <h3 className="text-lg font-medium text-midnight mb-4">Generating Your Share Page</h3>
                <div className="space-y-6">
                  <div className="flex items-center justify-center gap-4">
                    <div className="w-64 bg-sand/20 h-2 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-electric transition-all duration-500" 
                        style={{ 
                          width: `${(!generatingSummary && !generatingQuestions && !generatingCommentary && !generatingApplications) ? '100%' : 
                                  ((!generatingSummary && !generatingQuestions) || (!generatingCommentary && !generatingApplications)) ? '75%' :
                                  (!generatingSummary || !generatingQuestions || !generatingCommentary || !generatingApplications) ? '50%' : '25%'}%` 
                        }}
                      />
                    </div>
                    <span className="text-sm text-slate">
                      {(!generatingSummary && !generatingQuestions && !generatingCommentary && !generatingApplications) ? 'Creating page...' : 
                       'Generating content...'}
                    </span>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${!generatingSummary ? 'bg-emerald/10 text-emerald' : 'bg-sand/10 text-slate'}`}>
                        {!generatingSummary ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        )}
                      </div>
                      <span className="text-midnight">Generate Sermon Summary</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${!generatingQuestions ? 'bg-emerald/10 text-emerald' : 'bg-sand/10 text-slate'}`}>
                        {!generatingQuestions ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        )}
                      </div>
                      <span className="text-midnight">Generate Study Questions</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${!generatingCommentary ? 'bg-emerald/10 text-emerald' : 'bg-sand/10 text-slate'}`}>
                        {!generatingCommentary ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        )}
                      </div>
                      <span className="text-midnight">Generate Commentary Resources</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${!generatingApplications ? 'bg-emerald/10 text-emerald' : 'bg-sand/10 text-slate'}`}>
                        {!generatingApplications ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        )}
                      </div>
                      <span className="text-midnight">Generate Application Points</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${(!generatingSummary && !generatingQuestions && !generatingCommentary && !generatingApplications) ? 'bg-emerald/10 text-emerald' : 'bg-sand/10 text-slate'}`}>
                        {(!generatingSummary && !generatingQuestions && !generatingCommentary && !generatingApplications) ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        )}
                      </div>
                      <span className="text-midnight">Create Share Page</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'complete' && shareUrl && (
            <div className="space-y-6">
              <div className="bg-emerald/10 p-6 rounded-lg text-center">
                <CheckCircle className="h-12 w-12 text-emerald mx-auto mb-4" />
                <h3 className="text-lg font-medium text-midnight mb-2">Share Page Created!</h3>
                <p className="text-slate mb-6">
                  Your sermon share page has been created and is ready to share.
                </p>

                <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-sand/20 mb-4">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 bg-transparent focus:outline-none text-midnight"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="px-3 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 flex items-center"
                    type="button"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>

                <div className="flex justify-center">
                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-midnight text-white rounded-lg hover:bg-midnight/90 flex items-center"
                  >
                    View Page
                  </a>
                </div>
              </div>

              <div className="bg-sand/10 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-midnight mb-4">Additional Study Resources</h3>
                <p className="text-slate mb-6">
                  We've created additional resources to help with personal and group study of this sermon.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-sand/20">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="bg-electric/10 p-2 rounded-lg">
                        <MessageSquare className="h-5 w-5 text-electric" />
                      </div>
                      <div>
                        <h4 className="font-medium text-midnight">Small Group Guide</h4>
                        <p className="text-sm text-slate">Discussion questions and sermon summary for group study</p>
                      </div>
                    </div>
                    <button
                      onClick={handleDownloadSmallGroupGuide}
                      className="w-full px-3 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 flex items-center justify-center"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Guide
                    </button>
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-sand/20">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="bg-emerald/10 p-2 rounded-lg">
                        <BookOpen className="h-5 w-5 text-emerald" />
                      </div>
                      <div>
                        <h4 className="font-medium text-midnight">Personal Study Guide</h4>
                        <p className="text-sm text-slate">Commentary, application points, and reflection space</p>
                      </div>
                    </div>
                    <button
                      onClick={handleDownloadPersonalStudyGuide}
                      className="w-full px-3 py-2 bg-emerald text-white rounded-lg hover:bg-emerald/90 flex items-center justify-center"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Guide
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => {
                    setStep('initial');
                    setShareUrl(null);
                    setSummary(null);
                    setQuestions(null);
                    setCommentary(null);
                    setApplications(null);
                    setSmallGroupGuideUrl(null);
                    setPersonalStudyGuideUrl(null);
                  }}
                  className="px-4 py-2 bg-sand/20 text-slate rounded-lg hover:bg-sand/30 flex items-center"
                  type="button"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate Page
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-slate hover:text-midnight"
                  type="button"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}